import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserCircleIcon, 
  EnvelopeIcon, 
  PhoneIcon, 
  BuildingOffice2Icon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import ProfilePictureUpload from './ProfilePictureUpload';
import { auth } from '@obis/firebase-config';
import { updateProfile } from 'firebase/auth';

const UserProfile: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        navigate('/login');
        return;
      }

      const token = await currentUser.getIdToken();
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';
      const response = await fetch(`${API_URL}/users/${currentUser.uid}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setUser(data.data);
        setFormData({ name: data.data.name || '', phone: data.data.phone || '' });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = (photoURL: string) => {
    setUser({ ...user, profilePictureUrl: photoURL });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const token = await currentUser.getIdToken();
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';
      const response = await fetch(`${API_URL}/users/${currentUser.uid}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        // Update Firebase Auth displayName to sync with header
        if (formData.name) {
          await updateProfile(currentUser, {
            displayName: formData.name
          });
        }
        
        setUser({ ...user, ...formData });
        setEditing(false);
        alert('Profile updated successfully! Please refresh the page to see changes in the header.');
      } else {
        alert('Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({ name: user.name || '', phone: user.phone || '' });
    setEditing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">User not found</p>
      </div>
    );
  }

  const getRoleBadge = (role: string) => {
    const badges: { [key: string]: string } = {
      superadmin: 'bg-gradient-to-r from-purple-500 to-pink-600 text-white',
      admin: 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white',
      admissions_manager: 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white',
      admissions_counselor: 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white',
      marketing_staff: 'bg-gradient-to-r from-orange-500 to-red-600 text-white',
      principal: 'bg-gradient-to-r from-amber-500 to-yellow-600 text-white',
      alumni_coordinator: 'bg-gradient-to-r from-rose-500 to-pink-600 text-white'
    };
    return badges[role] || 'bg-gray-200 text-gray-800';
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
        <p className="text-gray-600">Manage your personal information</p>
      </div>

      {/* Profile Card */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Profile Picture Section */}
          <div className="flex-shrink-0">
            <ProfilePictureUpload
              currentPhotoURL={user.profilePictureUrl}
              userId={user.userId}
              onUploadComplete={handlePhotoUpload}
            />
          </div>

          {/* User Info Section */}
          <div className="flex-1 space-y-6">
            <div className="flex justify-between items-start">
              <div>
                {editing ? (
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="text-2xl font-bold text-gray-900 mb-2 border-b-2 border-blue-500 focus:outline-none"
                    placeholder="Your Name"
                  />
                ) : (
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{user.name}</h2>
                )}
                <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadge(user.role)}`}>
                  {user.role.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              {!editing ? (
                <button
                  onClick={() => setEditing(true)}
                  className="btn-outline"
                >
                  Edit Profile
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="btn-primary"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <EnvelopeIcon className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-sm font-medium text-gray-900">{user.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <PhoneIcon className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Phone</p>
                  {editing ? (
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="text-sm font-medium text-gray-900 border-b border-gray-300 focus:border-blue-500 focus:outline-none w-full"
                      placeholder="+91XXXXXXXXXX"
                    />
                  ) : (
                    <p className="text-sm font-medium text-gray-900">{user.phone || 'Not set'}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <BuildingOffice2Icon className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Branch</p>
                  <p className="text-sm font-medium text-gray-900 capitalize">{user.branchId}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <ShieldCheckIcon className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <p className="text-sm font-medium text-gray-900">
                    {user.isActive ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                Member since {new Date(user.createdAt).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Info Card */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>
        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">User ID</span>
            <span className="text-sm font-medium text-gray-900">{user.userId}</span>
          </div>
          {user.lastLogin && (
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Last Login</span>
              <span className="text-sm font-medium text-gray-900">
                {new Date(user.lastLogin).toLocaleString()}
              </span>
            </div>
          )}
          <div className="flex justify-between py-2">
            <span className="text-sm text-gray-600">Account Status</span>
            <span className={`text-sm font-medium ${user.isActive ? 'text-green-600' : 'text-red-600'}`}>
              {user.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
